import FuseAnimate from '@/@fuse/core/FuseAnimate';
import React from 'react';
import PageHeader from 'shared/components/PageHeader/PageHeader';
import { useForm, Controller } from 'react-hook-form';
import StyledInput from 'shared/components/StyledInput';
import StyledSearchSelect from 'shared/components/StyledSearchSelect';
import SquaredButton from 'shared/components/SquaredButton';
import { useState, useEffect } from 'react';
import { getMyCompanies } from '@/app/services/Company/companyService';
import { getMySchools, getRoomsBySchool } from '@/app/services/schools/schools';
import * as Actions from 'app/store/actions';
import { useDispatch } from 'react-redux';
import TwCheckbox from 'shared/components/TwCheckbox';
import { Divider } from '@material-ui/core';
import { registerCamera } from '@/app/services/camera';
const CameraRegistration = ({ setActiveTab }) => {
	const dispatch = useDispatch();
	const [searchTimeout, setSearchTimeout] = useState(null);
	const [companiesLoading, setCompaniesLoading] = useState(false);
	const [companies, setCompanies] = useState([]);
	const [schoolsLoading, setSchoolsLoading] = useState(false);
	const [schools, setSchools] = useState([]);
	const [roomsLoading, setRoomsLoading] = useState(false);
	const [rooms, setRooms] = useState([]);
	const [submitting, setSubmitting] = useState(false);

	const { control, handleSubmit, reset, watch, setValue } = useForm({
		defaultValues: {
			camera_id: '',
			company: null,
			school: null,
			room_name: null,
			admin_only: false,
			show_in_facelink: false,
			show_to_families: false,
		},
	});

	const selectedCompany = watch('company');

	const handleSearchCompanies = async (searchTerm) => {
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}

		const timeoutId = setTimeout(async () => {
			setCompaniesLoading(true);
			try {
				const res = await getMyCompanies(1, false, searchTerm || '');
				const mappedResults = res.data.data.map((company) => ({
					label: company.name,
					value: company.id,
				}));
				setCompanies(mappedResults);
			} catch (error) {
				console.error('Failed to fetch companies', error);
			}
			setCompaniesLoading(false);
		}, 300);

		setSearchTimeout(timeoutId);
	};

	const handleSearchSchools = async (searchTerm) => {
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}

		const timeoutId = setTimeout(async () => {
			setSchoolsLoading(true);
			try {
				const res = await getMySchools(searchTerm || '', 1, selectedCompany?.value || null);
				const mappedResults = res.data.data.map((school) => ({
					label: school.name,
					value: school.id,
				}));
				setSchools(mappedResults);
			} catch (error) {
				console.error('Failed to fetch schools', error);
			}
			setSchoolsLoading(false);
		}, 300);

		setSearchTimeout(timeoutId);
	};

	const handleGetRooms = async (schoolId) => {
		if (!schoolId) {
			setRooms([]);
			return;
		}

		setRoomsLoading(true);
		try {
			const res = await getRoomsBySchool(schoolId);
			const mappedResults = res.data.map((room) => ({
				label: room.name,
				value: room.id,
			}));
			setRooms(mappedResults);
		} catch (error) {
			console.error('Failed to fetch rooms', error);
			setRooms([]);
		}
		setRoomsLoading(false);
	};

	const handleCompanyChange = (newValue) => {
		setValue('company', newValue);
		setValue('school', null);
		setValue('room_name', null);
	};

	const handleSchoolChange = (newValue) => {
		setValue('school', newValue);
		setValue('room_name', null);
		if (newValue?.value) {
			handleGetRooms(newValue.value);
		} else {
			setRooms([]);
		}
	};

	// Reset school selection when company changes
	useEffect(() => {
		if (!selectedCompany) {
			setSchools([]);
			reset((formValues) => ({
				...formValues,
				school: null,
				room_name: null,
			}));
			return;
		}

		setSchoolsLoading(true);
		getMySchools('', 1, selectedCompany.value)
			.then((res) => {
				const mappedResults = res.data.data.map((school) => ({
					label: school.name,
					value: school.id,
				}));
				setSchools(mappedResults);
			})
			.catch((error) => {
				console.error('Failed to fetch schools for company', error);
				setSchools([]);
			})
			.finally(() => {
				setSchoolsLoading(false);
			});
	}, [selectedCompany]);

	const onSubmit = (data) => {
		setSubmitting(true);
		const payload = {
			camera_id: data.camera_id,
			company_id: data.company?.value,
			school_id: data.school?.value,
			room_id: data.room_name?.value,
			admin: data.admin_only,
			family: data.show_to_families,
			facelink: data.show_in_facelink,
			active: true,
		};

		registerCamera(payload)
			.then(() => {
				dispatch(
					Actions.showMessage({
						message: 'Camera added successfully',
						variant: 'success',
					})
				);
				setActiveTab('cameras');
			})
			.catch((error) => {
				dispatch(
					Actions.showMessage({
						message: error?.response?.data?.message || 'Failed to register camera',
						variant: 'error',
					})
				);
			})
			.finally(() => {
				setSubmitting(false);
			});
	};

	const handleAdminOnlyChange = (checked) => {
		setValue('admin_only', checked);
		if (checked) {
			// If admin only is checked, uncheck the other two
			setValue('show_in_facelink', false);
			setValue('show_to_families', false);
		}
	};

	const handleOtherCheckboxChange = (field, checked) => {
		setValue(field, checked);
		if (checked) {
			// If either facelink or families is checked, uncheck admin only
			setValue('admin_only', false);
		}
	};

	return (
		<FuseAnimate animation="transition.slideLeftIn" duration={600}>
			<div className=" mb-[162px] mx-60">
				<div className="flex justify-between items-end my-[40px]">
					<PageHeader onBack={() => setActiveTab('cameras')} title="Camera Registration" />
				</div>
				<div className="mx-auto rounded-[28px] bg-white p-16">
					<div className="shrink-0 mb-20">
						<span className="font-bold add-camera-heading">Camera Information</span>
					</div>
					<form onSubmit={handleSubmit(onSubmit)}>
						<div className="flex flex-col gap-40 p-16 rounded-[28px]">
							<Controller
								control={control}
								name="camera_id"
								render={({ field }) => <StyledInput label="Camera ID" {...field} />}
							/>
							<div className="flex flex-row flex-wrap gap-x-24 gap-y-[44px]">
								<Controller
									control={control}
									name="company"
									render={({ field: { value } }) => (
										<StyledSearchSelect
											label="Company"
											loading={companiesLoading}
											onChange={handleCompanyChange}
											onInputChange={(searchTerm) => handleSearchCompanies(searchTerm)}
											options={companies}
											value={value}
											widthClass="w-full md:w-auto"
										/>
									)}
								/>
								<Controller
									control={control}
									name="school"
									render={({ field: { value } }) => (
										<StyledSearchSelect
											label="School"
											loading={schoolsLoading}
											onChange={handleSchoolChange}
											onInputChange={(searchTerm) => handleSearchSchools(searchTerm)}
											options={schools}
											value={value}
											widthClass="w-full md:w-auto"
										/>
									)}
								/>
								<Controller
									control={control}
									name="room_name"
									render={({ field: { onChange, value } }) => {
										console.log(rooms);
										return (
											<StyledSearchSelect
												label="Room Name"
												loading={roomsLoading}
												onChange={onChange}
												options={rooms}
												value={value}
												widthClass="w-full md:w-auto"
											/>
										);
									}}
								/>
							</div>
							<div className="flex flex-row flex-wrap gap-20">
								<Controller
									control={control}
									name="admin_only"
									render={({ field: { value } }) => (
										<TwCheckbox
											checked={value}
											id="admin_only"
											label="Admin Only"
											onChange={(e) => handleAdminOnlyChange(e.target.checked)}
										/>
									)}
								/>
								<Divider flexItem orientation="vertical" />
								<Controller
									control={control}
									name="show_in_facelink"
									render={({ field: { value } }) => (
										<TwCheckbox
											checked={value}
											id="show_in_facelink"
											label="Show in Facelink™"
											onChange={(e) =>
												handleOtherCheckboxChange('show_in_facelink', e.target.checked)
											}
										/>
									)}
								/>
								<Controller
									control={control}
									name="show_to_families"
									render={({ field: { value } }) => (
										<TwCheckbox
											checked={value}
											id="show_to_families"
											label="Show to Families"
											onChange={(e) =>
												handleOtherCheckboxChange('show_to_families', e.target.checked)
											}
										/>
									)}
								/>
							</div>
						</div>
						<div className="flex justify-end gap-16 mt-40">
							{submitting ? (
								<div className="flex justify-center">
									<div className="animate-spin rounded-full h-32 w-32 border-b-2 border-pd-blue" />
								</div>
							) : (
								<>
									<SquaredButton
										onClick={() => setActiveTab('cameras')}
										size="xl"
										type="button"
										variant="outline"
									>
										Cancel
									</SquaredButton>
									<SquaredButton
										disabled={submitting}
										onClick={handleSubmit(onSubmit)}
										size="xl"
										type="submit"
										variant="filled"
									>
										Add Camera
									</SquaredButton>
								</>
							)}
						</div>
					</form>
				</div>
			</div>
		</FuseAnimate>
	);
};

export default CameraRegistration;
